import type { SharedV4ProviderOptions } from "@ai-sdk/provider";
import { z } from "zod";
// `prompts/file-info.ts` is a leaf module — no cycle back into config.
import { renderUnsupportedFileTag } from "../prompts/file-info";
import { isFileUploadPart } from "../types";

const MIME_TYPES = {
  images: ["image/jpeg", "image/png", "image/gif", "image/webp"],
  pdf: ["application/pdf"],
} as const;

/**
 * Anthropic rejects an image whose longest edge exceeds 2000px once a
 * request carries many images (`At least one of the image dimensions exceed
 * max allowed size for many-image requests`). The rejection is
 * deterministic, so the step runtime burns three retries and then fails the
 * turn — `prefetchFileUrls` downscales ahead of it instead. Every other
 * provider resizes server-side rather than rejecting, so they carry
 * `maxImageDimensionPx: null` and keep their full resolution.
 */
const ANTHROPIC_MAX_IMAGE_DIMENSION_PX = 2000;

/**
 * How much thinking a reasoning-capable model does before answering.
 * User-selectable in the model picker (Low / Standard / High), remembered
 * per model on the client, and resolved into agent settings by
 * `buildReasoningOptions`.
 *
 * @see https://ai-sdk.dev/docs/ai-sdk-core/reasoning
 */
export type ReasoningLevel = "low" | "medium" | "high";

/** Level used when the client sends none. "Standard" in the picker. */
export const defaultReasoningLevel: ReasoningLevel = "medium";

export const reasoningLevelSchema = z.enum(["low", "medium", "high"]);

export interface ModelConfig {
  /**
   * Prompt-cache read price as a fraction of the live input price. Default 1
   * (no discount) unless the provider's cache docs confirm a rate. Anthropic
   * explicit cache reads are 0.1. Do not copy that onto other providers.
   */
  cacheReadMultiplier?: number;
  /**
   * Prompt-cache write price as a fraction of the live input price. Default 1
   * (a miss costs a normal input) unless confirmed. Anthropic 5-minute cache
   * writes are 1.25.
   */
  cacheWriteMultiplier?: number;
  contextWindow: number;
  description: string;
  /**
   * Context size the soft compaction budget treats as the window (default
   * `DEFAULT_EFFECTIVE_CONTEXT_TOKENS`). Ignored when it isn't smaller
   * than `contextWindow`.
   */
  effectiveContextTokens?: number;
  gatewayId: string;

  /** Compaction budget overrides — see ADR-054 §3. */
  keepRecentTokens?: number;
  /**
   * Hard per-image pixel cap the provider enforces by *rejecting* the
   * request, or `null` when the provider silently downscales instead.
   * Only Anthropic rejects (2000px on the long edge once a request carries
   * many images); OpenAI and Google resize server-side, so forcing a
   * client-side downscale on them would throw away detail for nothing.
   * `prefetchFileUrls` skips the sharp dimension probe entirely when this
   * is `null`.
   */
  maxImageDimensionPx: number | null;
  name: string;
  /**
   * Provider-specific options that opt the model into returning (and thus
   * streaming) its reasoning/thinking text to the client. Merged into the
   * agent's `providerOptions` per turn — see `buildGatewayProviderOptions`'s
   * `extra` param and the chat workflow.
   *
   * Reasoning *depth* is not configured here: it comes from the top-level
   * `reasoning` option (see `ReasoningLevel`), and any effort/budget key set
   * in `providerOptions` would silently take full precedence over it. Only
   * non-effort options that surface the reasoning text belong in this field.
   *
   * @see https://vercel.com/docs/ai-gateway/models-and-providers/reasoning
   */
  reasoningProviderOptions?: SharedV4ProviderOptions;
  reserveTokens?: number;
  /**
   * Highest reasoning effort Auto routing may pick for this model; a higher
   * pick from the router is capped down to it. Absent = no cap. Applies only
   * to routed turns: a user who picks the model and a level themselves is not
   * limited. Exists because effort is a cost lever the router controls — long
   * high-effort answers dominate spend on the high tier.
   */
  smartAutoMaxReasoningLevel?: ReasoningLevel;
  supportedMimeTypes: readonly string[];
  /**
   * Whether the provider accepts HTTP(S) URLs directly for file/image parts.
   *
   * **Every model is `false` today** — see the `all models inline file URLs`
   * test below and the 2026-08-08 amendment to ADR-032. URL passthrough let
   * URL-bearing file parts reach `WorkflowAgent.stream`, whose `"use workflow"`
   * context blocks global `fetch`; any part the AI SDK then decided to
   * download (it treats a part with no `mediaType` as unsupported regardless
   * of the gateway's wildcard `supportedUrls`) threw a fatal
   * `AI_DownloadError`. Since
   * ADR-055 only raster images reach the provider as native file parts —
   * documents ship as `data-magnus-document` — so inlining costs far less
   * than it did when ADR-032 chose passthrough.
   *
   * Kept as a flag rather than deleted so re-enabling passthrough for a
   * future provider is a single deliberate edit.
   */
  supportsFileUrls: boolean;
  /**
   * Whether the model accepts a tunable `ReasoningLevel`. Drives the Thinking
   * section in the model picker and gates what `buildReasoningOptions` sends —
   * a provider with no reasoning support would just warn and ignore it.
   */
  supportsReasoningLevel: boolean;
  /**
   * Whether the AI Gateway can serve this model through a provider it holds a
   * zero-data-retention agreement with.
   *
   * `false` is a **deliberate exemption from our ZDR default**, not a capability
   * gap: all gateway traffic otherwise carries `zeroDataRetention: true` (see
   * `buildGatewayProviderOptions`), and the gateway hard-rejects a request whose
   * every candidate provider lacks an agreement — HTTP 400
   * `no_zdr_providers_available`, before a token of inference. That makes such a
   * model 100% broken rather than degraded, so the only two honest options are
   * this flag or leaving the model out of the catalog. Marking it `false` sends
   * that model's prompts and completions to a non-ZDR provider; check the data
   * classification of what users can put in chat before adding one.
   *
   * Required (not optional-defaulting-to-true) so adding a model forces the
   * decision, the same reasoning as `supportsFileUrls` in ADR-032 §5.
   *
   * Verify with a probe rather than by reading provider marketing: the gateway's
   * ZDR routing set comes from Vercel's negotiated agreements, not the
   * provider's own published policy.
   *
   * @see https://vercel.com/docs/ai-gateway/security-and-compliance/zdr
   */
  supportsZeroDataRetention: boolean;
  tag?: string;
}

/**
 * Anthropic is the one provider that carries the level in provider options
 * instead of the portable `reasoning` option, because setting `thinking` here
 * takes full precedence over the top-level option, so the level has to ride
 * along as `effort`.
 *
 * `display: 'omitted'` is deliberate: the model still thinks at the selected
 * effort and returns a signed, empty thinking block, and the client shows a
 * content-less "Thinking…" row that settles to "Thought for N seconds" when
 * the block ends. `'summarized'` streams the summary in one burst at the end
 * of thinking, and the first tool call or answer waits behind it: measured
 * +1.0-1.6s to first readable content and +0.5-0.7s to step completion, with
 * identical thinking tokens (docs/improvements/ttft-mitigation-spike.md).
 * Other providers stream their summaries progressively, so they keep them.
 *
 * `type: 'adaptive'` is required on 4.7+ (a fixed `budgetTokens` returns a 400)
 * and supported on 4.6; `low`/`medium`/`high` are accepted by every Anthropic
 * series we ship.
 *
 * @see https://vercel.com/docs/ai-gateway/models-and-providers/reasoning/anthropic
 */
const anthropicReasoningProviderOptions = (
  level: ReasoningLevel
): SharedV4ProviderOptions => ({
  anthropic: {
    thinking: { type: "adaptive", display: "omitted" },
    effort: level,
  },
});

/**
 * OpenAI reasoning options. GPT-5.x are reasoning models that omit their
 * thought process unless `reasoningSummary` is set; `'auto'` returns the
 * richest summary the model supports, streamed as `reasoning` parts. Depth
 * comes from the top-level `reasoning` option — `reasoningEffort` must stay
 * out of here or it would override the user's selected level.
 *
 * @see https://ai-sdk.dev/providers/ai-sdk-providers/openai
 */
const openaiReasoningProviderOptions: SharedV4ProviderOptions = {
  openai: {
    reasoningSummary: "auto",
  },
};

/**
 * Google Gemini thinking options. `includeThoughts: true` returns synthesized
 * thought summaries (streamed as `reasoning-delta` parts); `thinkingLevel` is
 * derived from the top-level `reasoning` option instead of being pinned here.
 *
 * @see https://vercel.com/docs/ai-gateway/models-and-providers/reasoning/google
 */
const googleReasoningProviderOptions: SharedV4ProviderOptions = {
  google: {
    thinkingConfig: { includeThoughts: true },
  },
};

export const modelConfigs = {
  "gpt-6.1-sol": {
    name: "GPT-6.1 Sol",
    description:
      "OpenAI's balanced model for coding, research, and agentic work",
    tag: "New",
    gatewayId: "openai/gpt-6.1-sol",
    contextWindow: 1_100_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    reasoningProviderOptions: openaiReasoningProviderOptions,
  },

  "gpt-6-luna": {
    name: "GPT-6 Luna",
    description: "Fast, cost-efficient OpenAI model for everyday tasks",
    tag: "New",
    gatewayId: "openai/gpt-6-luna",
    contextWindow: 1_100_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    reasoningProviderOptions: openaiReasoningProviderOptions,
  },

  "gpt-6-astra": {
    name: "GPT-6 Astra",
    description: "OpenAI's next-generation flagship for frontier agentic work",
    tag: "New",
    gatewayId: "openai/gpt-6-astra",
    contextWindow: 1_100_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    reasoningProviderOptions: openaiReasoningProviderOptions,
  },

  "claude-sonnet-5.5": {
    name: "Sonnet 5.5",
    description: "Anthropic's latest, strong at coding and agentic work",
    smartAutoMaxReasoningLevel: "medium",
    gatewayId: "anthropic/claude-sonnet-5.5",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: ANTHROPIC_MAX_IMAGE_DIMENSION_PX,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    cacheReadMultiplier: 0.1,
    cacheWriteMultiplier: 1.25,
  },
  "claude-sonnet-4.6": {
    name: "Sonnet 4.6",
    description: "Thoughtful and detailed, great for writing",
    gatewayId: "anthropic/claude-sonnet-4.6",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: ANTHROPIC_MAX_IMAGE_DIMENSION_PX,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    cacheReadMultiplier: 0.1,
    cacheWriteMultiplier: 1.25,
  },
  "claude-opus-5.5": {
    name: "Opus 5.5",
    description: "Most capable for complex tasks and analysis",
    tag: "Expensive",
    gatewayId: "anthropic/claude-opus-5.5",
    // Gateway catalog (2026-09-23): 1M context, 128k max output.
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: ANTHROPIC_MAX_IMAGE_DIMENSION_PX,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    cacheReadMultiplier: 0.1,
    cacheWriteMultiplier: 1.25,
  },
  "claude-haiku-5.5": {
    name: "Haiku 5.5",
    description: "Anthropic's fast, low-cost model for everyday tasks",
    tag: "New",
    gatewayId: "anthropic/claude-haiku-5.5",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: ANTHROPIC_MAX_IMAGE_DIMENSION_PX,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    cacheReadMultiplier: 0.1,
    cacheWriteMultiplier: 1.25,
  },

  "claude-fable-5.1": {
    name: "Fable 5.1",
    description:
      "Anthropic's powerful new agentic model, strong at coding and reasoning",
    tag: "New",
    gatewayId: "anthropic/claude-fable-5.1",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: ANTHROPIC_MAX_IMAGE_DIMENSION_PX,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    // Anthropic serves Fable only under a retention policy Vercel holds no ZDR
    // agreement for: the gateway refuses the request with HTTP 400
    // "anthropic/claude-fable-5.1 is ineligible for Zero Data Retention (ZDR).
    // This model retains data and cannot be used with ZDR configurations."
    // Probed 2026-09-18 — identical requests fail 400 with ZDR on and complete
    // normally with it off.
    supportsZeroDataRetention: false,
  },

  "gemini-4-argon": {
    name: "Gemini 4 Argon",
    description: "Google's latest, strong at research and reasoning",
    tag: "New",
    gatewayId: "google/gemini-4-argon",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    reasoningProviderOptions: googleReasoningProviderOptions,
  },

  "gemini-3.8-flash": {
    name: "Gemini 3.8 Flash",
    description: "Google's fast, low-latency model for everyday tasks",
    tag: "New",
    gatewayId: "google/gemini-3.8-flash",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    supportsReasoningLevel: true,
    supportsZeroDataRetention: true,
    reasoningProviderOptions: googleReasoningProviderOptions,
  },

  "grok-4.7": {
    name: "Grok 4.7",
    description:
      "SpaceXAI's frontier model, built for long-running agentic work",
    tag: "New",
    // The provider slug is `spacexai`, not `xai` — the gateway catalog moved
    // every Grok model under it, so `getProvider` (telemetry, the SDK model
    // list) reports `spacexai` from here on.
    gatewayId: "spacexai/grok-4.7",
    contextWindow: 500_000,
    // SpaceXAI accepts jpg/png image input only — gif and webp are rejected,
    // so this is deliberately narrower than `MIME_TYPES.images`. Like 4.6, 4.7
    // takes no PDF input; documents reach it as `data-magnus-document` the same
    // as on every other model.
    supportedMimeTypes: ["image/jpeg", "image/png"],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    // `reasoning_effort` accepts low/medium/high and can't be disabled;
    // reasoning summaries stream without an opt-in provider option.
    supportsReasoningLevel: true,
    // Carried over from 4.6, which the gateway catalog reported `zdr: "all"` /
    // `no_training: "all"` for: SpaceXAI is served ZDR-only, so no request
    // needs the exemption.
    supportsZeroDataRetention: true,
  },

  "muse-spark-1.3": {
    name: "Muse Spark 1.3",
    description: "Meta's coding-focused model for long-horizon dev workflows",
    tag: "New",
    gatewayId: "meta/muse-spark-1.3",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    // Carried over from 1.2: the model reasons heavily but returns only
    // `reasoning_details: [{ type: "reasoning.encrypted" }]`, and neither
    // `include_reasoning`, `reasoning.summary: "auto"`, nor
    // `openai.reasoningSummary` yielded plaintext, so there is no thinking text
    // to stream. The catalog now lists an effort scale for the Muse line, but
    // the picker's Thinking section stays off until a probe confirms the
    // gateway honors it.
    supportsReasoningLevel: false,
    // Meta is now served under a ZDR agreement (gateway catalog `zdr: "all"`),
    // so the exemption 1.2 needed is gone.
    supportsZeroDataRetention: true,
  },

  "muse-wonder-1.0": {
    name: "Muse Wonder",
    description:
      "Meta's frontier model for coding and long-running agentic work",
    tag: "New",
    gatewayId: "meta/muse-wonder-1.0",
    contextWindow: 1_000_000,
    supportedMimeTypes: [...MIME_TYPES.images, ...MIME_TYPES.pdf],
    maxImageDimensionPx: null,
    supportsFileUrls: false,
    // Same encrypted-only reasoning as Muse Spark; see `muse-spark-1.3`.
    supportsReasoningLevel: false,
    supportsZeroDataRetention: true,
  },
} as const satisfies Record<string, ModelConfig>;

export type ModelId = keyof typeof modelConfigs;

export const defaultModel: ModelId = "claude-haiku-5.5";

/** Auto routing's two tiers (ADR-127): a routed chat starts on `low` and may
 *  escalate to `high`. */
export type SmartAutoTier = "low" | "high";

export const smartAutoTiers: readonly SmartAutoTier[] = ["low", "high"];

/**
 * The model in each routing tier slot (ADR-127). Replacing either model is an
 * edit here and nowhere else: the routing prompt names no model.
 */
export const smartAutoModels: Readonly<Record<SmartAutoTier, ModelId>> = {
  low: "claude-haiku-5.5",
  high: "claude-sonnet-5.5",
};

/** The routing tier `id` fills, or `undefined` when it fills neither. */
export function getSmartAutoTier(id: string): SmartAutoTier | undefined {
  return smartAutoTiers.find((tier) => smartAutoModels[tier] === id);
}

/** A model's routed-effort ceiling, or `undefined` when uncapped. */
export function getSmartAutoMaxReasoningLevel(
  id: ModelId
): ReasoningLevel | undefined {
  const config: ModelConfig = modelConfigs[id];
  return config.smartAutoMaxReasoningLevel;
}
export const modelIdSchema = z
  .enum(Object.keys(modelConfigs) as [ModelId, ...ModelId[]])
  .default(defaultModel);

/**
 * Wire-level model selection: a concrete `ModelId` or `"auto"` (Jev routing
 * when `models.auto-route.enable` is on, else the surface default).
 * `"smart-auto"` is a deprecated alias for `"auto"`, kept for persisted
 * preferences and older SDK callers. The auto selections are resolved to a
 * real `ModelId` inside `loadChatContext` (see `routing.ts`) and are
 * client-facing only; nothing downstream of context load may ever see them.
 */
export const modelSelectionSchema = z.union([
  z.literal("auto"),
  z.literal("smart-auto"),
  modelIdSchema.removeDefault(),
]);

export const getContextWindow = (id: ModelId): number =>
  modelConfigs[id].contextWindow;

export const getModelConfig = (id: ModelId): ModelConfig => ({
  ...modelConfigs[id],
  contextWindow: getContextWindow(id),
});

export const getModelIds = (): ModelId[] =>
  Object.keys(modelConfigs) as ModelId[];

export const getSupportedMimeTypes = (id: ModelId): readonly string[] =>
  modelConfigs[id].supportedMimeTypes;

export const getSupportsFileUrls = (id: ModelId): boolean =>
  modelConfigs[id].supportsFileUrls;

export const getMaxImageDimensionPx = (id: ModelId): number | null =>
  modelConfigs[id].maxImageDimensionPx;

export const getSupportsReasoningLevel = (id: ModelId): boolean =>
  modelConfigs[id].supportsReasoningLevel;

export const getSupportsZeroDataRetention = (id: ModelId): boolean =>
  modelConfigs[id].supportsZeroDataRetention;

export const isMimeTypeSupported = (id: ModelId, mimeType: string): boolean =>
  (modelConfigs[id].supportedMimeTypes as readonly string[]).includes(mimeType);

export const getProvider = (id: ModelId): string =>
  modelConfigs[id].gatewayId.split("/")[0] ?? "unknown";

/**
 * Resolve a model + reasoning level into the two knobs the agent takes:
 * `reasoning` (the portable top-level option the gateway translates per
 * provider) and `providerOptions` (non-effort options that surface the
 * reasoning text).
 *
 * The two are never merged by the SDK — a reasoning-related provider option
 * wins outright — so exactly one of them carries the level. Anthropic uses
 * `providerOptions` because it also needs `thinking.display`; everyone else
 * uses the portable option. Models that don't support a tunable level get
 * their static options only.
 */
export function buildReasoningOptions(
  id: ModelId,
  level: ReasoningLevel = defaultReasoningLevel
): {
  providerOptions?: SharedV4ProviderOptions;
  reasoning?: ReasoningLevel;
} {
  const config = getModelConfig(id);
  if (!config.supportsReasoningLevel) {
    return { providerOptions: config.reasoningProviderOptions };
  }
  if (getProvider(id) === "anthropic") {
    return { providerOptions: anthropicReasoningProviderOptions(level) };
  }
  return {
    providerOptions: config.reasoningProviderOptions,
    reasoning: level,
  };
}

// Structural shape this function reads off each message part. The
// constraint is intentionally minimal: AI SDK's `UIMessagePart` is a
// discriminated union (text / file / data-${string} / tool / …) and any
// concrete shape we put here has to be a supertype of EVERY variant.
// Reading `mediaType` / `filename` / `url` is safe because they're either
// declared optional on file parts or absent entirely on others. The
// file-only `data: FileUploadPartData` payload is reached via the
// `isFileUploadPart` type guard below — never via the constraint, since
// that would leak the file-specific shape onto every part and reject
// real `UIMessage`s (whose `data-*` parts have `data: unknown`).
type FilterablePart = {
  type: string;
  mediaType?: string;
  filename?: string;
  url?: string;
  // `data` is `unknown` on AI SDK `UIMessage` data-parts, so accept that
  // shape here and narrow at the read site below.
  data?: unknown;
};

function readUploadIdFromData(data: unknown): string | undefined {
  if (typeof data !== "object" || data === null) {
    return undefined;
  }
  const value = (data as { uploadId?: unknown }).uploadId;
  return typeof value === "string" ? value : undefined;
}

export function filterMessagesForModel<
  T extends {
    role: string;
    parts?: FilterablePart[];
  },
>(messages: T[], modelId: ModelId): T[] {
  return messages.map((message): T => {
    if (message.role !== "user" || !message.parts?.length) {
      return message;
    }

    const processedParts: typeof message.parts = [];
    const unsupportedFiles: Array<{
      url?: string;
      filename?: string;
      mediaType: string;
      uploadId?: string;
    }> = [];

    for (const part of message.parts) {
      if (isFileUploadPart(part)) {
        const mediaType = part.mediaType ?? "";
        if (isMimeTypeSupported(modelId, mediaType)) {
          processedParts.push(part);
        } else {
          unsupportedFiles.push({
            url: part.url,
            filename: part.filename,
            mediaType,
            uploadId: readUploadIdFromData(part.data),
          });
        }
      } else {
        processedParts.push(part);
      }
    }

    if (unsupportedFiles.length > 0) {
      // Emit a `<file-info />` tag per stripped file (same contract as
      // `prepareMessagePartsForModel`) so the model has the prefixed
      // sandbox path and can `cat` the file directly. Using the
      // friendlier user-facing filename here previously caused the model to
      // `cat /home/user/current-chat/uploads/<friendly>` and fail —
      // the actual blob is named `<nanoid>-<friendly>`.
      const fileTags = unsupportedFiles
        .map(renderUnsupportedFileTag)
        .join("\n");
      processedParts.push({
        type: "text",
        text: fileTags,
      } as (typeof message.parts)[number]);
    }

    return { ...message, parts: processedParts };
  });
}

export function filterInvalidToolCalls<
  T extends {
    id: string;
    role: string;
    parts?: Array<{ type: string; state?: string }>;
    metadata?: unknown;
  },
>(messages: T[], validToolNames: Set<string>): T[] {
  return messages.map((m) => {
    const filteredParts = m.parts?.filter((part) => {
      if (!part.type.startsWith("tool-")) {
        return true;
      }
      // Completed calls are historical records; dropping them deletes real
      // tool context and, worse, orphans the `thinking` blocks interleaved
      // with them — Anthropic then rejects every subsequent request with
      // "`thinking` … blocks in the latest assistant message cannot be
      // modified". See
      // docs/incidents/2026-07-11-provider-unavailable-thinking-blocks.md.
      //
      // Note: progressive-disclosure proxy calls are persisted under their
      // inner `${capabilityId}_${toolName}` identity (ADR-057), which is NOT
      // in the top-level tool set. Those are collapsed back to
      // `callCapabilityTool` before this filter runs (see
      // `collapseProgressiveDisclosureInMessages` in
      // `prepareMessagesForModel`), so the model sees a repeatable, callable
      // identity rather than imitating an inner name and hitting
      // `AI_NoSuchToolError`.
      if (part.state === "output-available" || part.state === "output-error") {
        return true;
      }
      const toolName = part.type.slice(5); // Remove "tool-" prefix
      return validToolNames.has(toolName);
    });

    return filteredParts?.length === m.parts?.length
      ? m
      : { ...m, parts: filteredParts };
  });
}

/** `errorText` `repairIncompleteToolCalls` writes on a call that never ran
 *  (stream stopped mid-call). Auto routing does not count it as a tool
 *  failure. */
export const CANCELLED_TOOL_ERROR_TEXT = "Tool execution was cancelled.";

const INCOMPLETE_TOOL_STATES = new Set([
  "call",
  "partial-call",
  "input-streaming",
  "input-available",
]);

/**
 * Returns true if any message carries a tool invocation left in a
 * non-terminal state (`call` / `partial-call` / `input-streaming` /
 * `input-available`). That means the turn ended while a tool call was
 * still streaming or awaiting a result — i.e. the stream stopped
 * mid-tool-call rather than at a clean stopping point.
 *
 * Distinct from `repairIncompleteToolCalls`, which mutates those parts
 * into `output-error` so the prompt stays valid; this is the read-only
 * detector the workflow uses to classify the turn's outcome before
 * deciding which persistence path to take.
 */
export function hasIncompleteToolCalls<
  T extends {
    role: string;
    parts?: Array<{ type: string; state?: string }>;
  },
>(messages: T[]): boolean {
  return messages.some(
    (m) =>
      m.role === "assistant" &&
      (m.parts ?? []).some(
        (part) =>
          part.type.startsWith("tool-") &&
          INCOMPLETE_TOOL_STATES.has(part.state ?? "")
      )
  );
}

/**
 * Patches tool invocations left in non-terminal states (e.g. after an abort)
 * so every `tool_use` has a matching `tool_result` when sent to the model.
 *
 * Also backfills a placeholder `input` on the repaired part. A call aborted
 * in `input-streaming` has a `toolCallId` + `toolName` but no arguments yet,
 * so the persisted `tool_use` is missing `input` entirely. Anthropic rejects
 * such a block (`messages.N.content.M.tool_use.input: Field required`), and
 * because the bad part is now stored, the whole chat fails deterministically
 * on every later turn. `{}` keeps the block valid; the paired `output-error`
 * result already tells the model the call didn't run.
 */
export function repairIncompleteToolCalls<
  T extends {
    id: string;
    role: string;
    parts?: Array<{ type: string; state?: string }>;
    metadata?: unknown;
  },
>(messages: T[]): T[] {
  return messages.map((m) => {
    let changed = false;
    const repairedParts = m.parts?.map((part) => {
      if (!part.type.startsWith("tool-")) {
        return part;
      }
      if (!INCOMPLETE_TOOL_STATES.has(part.state ?? "")) {
        return part;
      }
      changed = true;
      const existingInput = (part as { input?: unknown }).input;
      return {
        ...part,
        input: existingInput ?? {},
        state: "output-error" as const,
        errorText: CANCELLED_TOOL_ERROR_TEXT,
      };
    });
    return changed ? ({ ...m, parts: repairedParts } as T) : m;
  });
}

/**
 * Drop `reasoning` parts from assistant messages before persisted history
 * is converted into a provider prompt.
 *
 * Reasoning blocks are only meaningful *within* the turn that produced
 * them (the in-turn tool loop is handled by the agent SDK, not by this
 * history path). Replaying them on later turns is at best dead weight and
 * at worst fatal: Anthropic cryptographically signs extended-thinking
 * blocks and rejects the whole request when a replayed block no longer
 * matches its signature context — e.g. when a tool call that sat between
 * two thinking blocks was dropped from history (zombie-belt
 * reconstruction, tool-set changes) the API returns
 * "`thinking` or `redacted_thinking` blocks in the latest assistant
 * message cannot be modified" and the chat is wedged on every subsequent
 * turn. See
 * `docs/incidents/2026-07-11-provider-unavailable-thinking-blocks.md`.
 *
 * Nothing of value is lost: the gateway streams no thinking text for
 * Anthropic models (signature only), and providers never require prior
 * turns' reasoning to be replayed.
 */
export function stripHistoricalReasoning<
  T extends {
    role: string;
    parts?: Array<{ type: string }>;
  },
>(messages: T[]): T[] {
  return messages.map((m) => {
    if (m.role !== "assistant" || !m.parts?.length) {
      return m;
    }
    const parts = m.parts.filter((part) => part.type !== "reasoning");
    return parts.length === m.parts.length ? m : { ...m, parts };
  });
}
